import { ApiSchemaInsuranceType } from "./../../models/api/insuranceType";
import { SWRResponse } from "swr";
import { ApiSchemaPatient } from "../../models/api";
import { AxiosAuth } from "../axios";
import {
  useExtendedApiAccess,
  CreateAccessObject,
} from "./useExtendedApiAccess";
import { AppRouteApi } from "../../config/appRoute";
import { PaginatedData } from "../../models/api";
import { useAppSelector } from "../redux";
import { useRef, useEffect } from "react";
import { ApiResponseContainer } from "../../models/api";
import { useAppDispatch } from "../redux/ReduxStore";
import { ActionsPatientTable } from "../redux/slices/slicePatientTable";
import { showErrorToast } from "../../components/toast.component";
import { StorageAuth } from "../localStorage";

const CacheIds = {
  GetAll: AppRouteApi.Patient.All(),
  GetDropdown: AppRouteApi.Patient.List(),
  ById: AppRouteApi.Patient.ById(""),
  GetAllInsuranceTypesByCompanyId: AppRouteApi.InsuranceTypes.ByCompanyId(""),
};

function useApiPatientGetAll(
  companyId?: string
): SWRResponse<ApiSchemaPatient[] | undefined, Error> {
  const { pageSize, status } = useAppSelector((state) => state.patientTable);
  const dispatch = useAppDispatch();
  const isMounted = useRef(false);

  const api = useExtendedApiAccess<ApiSchemaPatient[]>(CacheIds.GetAll, () =>
    AxiosAuth.get(CacheIds.GetAll, {
      params: {
        CompanyId: companyId ? companyId : StorageAuth.CompanyId,
        PatientStatus: status,
      },
    })
  );

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
    } else {
      (async () => {
        dispatch(ActionsPatientTable.setIsLoading(true));
        const res = await AxiosAuth.get<
          ApiResponseContainer<ApiSchemaPatient[]>
        >(CacheIds.GetAll, {
          params: {
            CompanyId: companyId ? companyId : StorageAuth.CompanyId,
            PatientStatus: status,
          },
        });

        if (res.data.isSuccess) {
          api.mutate(res.data.data, false);
        }
        dispatch(ActionsPatientTable.setIsLoading(false));
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSize, companyId, status]);

  return api;
}

function useApiPatientOfCompanyGetAll(
  companyId?: string
): SWRResponse<ApiSchemaPatient[] | undefined, Error> {
  const api = useExtendedApiAccess<ApiSchemaPatient[]>(
    CacheIds.GetDropdown,
    () =>
      AxiosAuth.get(CacheIds.GetDropdown, {
        params: {
          CompanyId: companyId ? companyId : StorageAuth.CompanyId,
        },
      })
  );

  return api;
}

function useApiPatientById(
  id: string
): SWRResponse<ApiSchemaPatient | undefined, Error> {
  return useExtendedApiAccess<ApiSchemaPatient>(CacheIds.ById, () =>
    AxiosAuth.get(AppRouteApi.Patient.ById(id))
  );
}

function useApiInsurancesTypes(
  companyId: string
): SWRResponse<ApiSchemaInsuranceType[] | undefined, Error> {
  return useExtendedApiAccess<ApiSchemaInsuranceType[]>(
    CacheIds.GetAllInsuranceTypesByCompanyId,
    () => AxiosAuth.get(AppRouteApi.InsuranceTypes.ByCompanyId(companyId))
  );
}

const AccessObject = {
  GetAll: CreateAccessObject<ApiSchemaPatient[], typeof useApiPatientGetAll>(
    CacheIds.GetAll,
    useApiPatientGetAll
  ),

  ById: CreateAccessObject<ApiSchemaPatient, typeof useApiPatientById>(
    CacheIds.ById,
    useApiPatientById
  ),

  GetAllInsuranceTypes: CreateAccessObject<
    ApiSchemaInsuranceType[],
    typeof useApiInsurancesTypes
  >(CacheIds.GetAllInsuranceTypesByCompanyId, useApiInsurancesTypes),

  GetAllFromCompany: CreateAccessObject<
    PaginatedData<ApiSchemaPatient>,
    typeof useApiPatientOfCompanyGetAll
  >(CacheIds.GetDropdown, useApiPatientOfCompanyGetAll),
};

async function AddPatient(
  patient: FormData
): Promise<ApiResponseContainer<ApiSchemaPatient>> {
  try {
    const response = await AxiosAuth.post<
      ApiResponseContainer<ApiSchemaPatient>
    >(AppRouteApi.Patient.Root(), patient);
    if (response.data.isSuccess) {
      const updAction = (
        res?: ApiSchemaPatient[]
      ): ApiSchemaPatient[] | undefined => {
        if (res) return res;
      };
      AccessObject.GetAll.Mutate(updAction());
    }
    return response.data;
  } catch (e: any) {
    return showErrorToast({ isSuccess: false, ...JSON.parse(e.message).data });
  }
}

async function UpdatePatient(
  patient: FormData
): Promise<ApiResponseContainer<ApiSchemaPatient>> {
  try {
    const response = await AxiosAuth.put<
      ApiResponseContainer<ApiSchemaPatient>
    >(AppRouteApi.Patient.Root(), patient);
    if (response.data.isSuccess) {
      const updAction = (
        res?: ApiSchemaPatient[]
      ): ApiSchemaPatient[] | undefined => {
        if (res) return res;
      };
      AccessObject.GetAll.Mutate(updAction());
    }
    return response.data;
  } catch (e: any) {
    return showErrorToast({ isSuccess: false, ...JSON.parse(e.message).data });
  }
}

async function DeletePatient(ids: string[]): Promise<boolean> {
  try {
    const response = await AxiosAuth.delete<ApiResponseContainer<boolean>>(
      AppRouteApi.Patient.Delete(ids)
    );
    if (response.data.isSuccess) return true;
  } catch (e: any) {
    showErrorToast({ isSuccess: false, ...JSON.parse(e.message).data });
  }
  return false;
}

async function ActivateDeactivatePatient(
  id: string,
  deactivate: boolean
): Promise<ApiResponseContainer<ApiSchemaPatient>> {
  try {
    const response = await AxiosAuth.put<
      ApiResponseContainer<ApiSchemaPatient>
    >(
      AppRouteApi.Patient.ActivateDeactivate(id),
      {},
      {
        params: {
          deactivate: deactivate,
        },
      }
    );
    if (response.data.isSuccess) {
      const updAction = (
        res?: ApiSchemaPatient[]
      ): ApiSchemaPatient[] | undefined => {
        if (res) return res;
      };
      AccessObject.GetAll.Mutate(updAction());
    }
    return response.data;
  } catch (e: any) {
    return showErrorToast({ isSuccess: false, ...JSON.parse(e.message).data });
  }
}

async function ChangeSignatureRequirement(
  id: string,
  isSignatureRequired: boolean
): Promise<ApiResponseContainer<ApiSchemaPatient>> {
  try {
    const response = await AxiosAuth.put<
      ApiResponseContainer<ApiSchemaPatient>
    >(
      AppRouteApi.Patient.ChangeSignatureRequirement(id),
      {},
      {
        params: {
          isRequired: isSignatureRequired,
        },
      }
    );
    if (response.data.isSuccess) {
      const updAction = (
        res?: ApiSchemaPatient[]
      ): ApiSchemaPatient[] | undefined => {
        if (res) return res;
      };
      AccessObject.GetAll.Mutate(updAction());
    }
    return response.data;
  } catch (e: any) {
    return showErrorToast({ isSuccess: false, ...JSON.parse(e.message).data });
  }
}

export const ApiPatient = {
  GetAll: AccessObject.GetAll,
  ById: AccessObject.ById,
  GetAllInsuranceTypes: AccessObject.GetAllInsuranceTypes,
  GetAllFromCompany: AccessObject.GetAllFromCompany,
  Add: AddPatient,
  Delete: DeletePatient,
  Update: UpdatePatient,
  ActivateDeactivate: ActivateDeactivatePatient,
  ChangeSignatureRequirement: ChangeSignatureRequirement,
};
